import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PasswordInput } from "../components/PasswordInput.js";

type AcceptResult = { ok: true; context: string; role: string };
type ViewState = "probing" | "accepted" | "login_required" | "password_required" | "forbidden" | "error";

export function AcceptHubInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [view, setView] = useState<ViewState>("probing");
  const [result, setResult] = useState<AcceptResult | null>(null);
  const [inviteEmail, setInviteEmail] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function tryAccept(pw?: string) {
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, string> = { token };
      if (pw) body.password = pw;
      const data = await apiFetch<AcceptResult>("/api/auth/invites/accept", {
        method: "POST",
        body
      });
      setResult(data);
      setView("accepted");
    } catch (err) {
      if (err instanceof ApiRequestError) {
        if (err.code === "login_required") {
          setInviteEmail((err.data.email as string) ?? null);
          setView("login_required");
        } else if (err.code === "password_required") {
          setInviteEmail((err.data.email as string) ?? null);
          setView("password_required");
        } else if (err.code === "forbidden") {
          setView("forbidden");
        } else {
          setError(err.message);
          setView("error");
        }
      } else {
        setError(err instanceof Error ? err.message : String(err));
        setView("error");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (token) void tryAccept();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!token) {
    return (
      <div className="centerPage">
        <div className="card pageNarrow wFull">
          <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>Invalid link</div>
          <div className="muted">This invite link is missing a token.</div>
          <div style={{ marginTop: 16 }}>
            <Link to="/login" className="btn btnPrimary">Sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  const roleLabel = result?.role === "hub_admin" ? "Hub administrator" : "Hub operator";

  return (
    <div className="centerPage">
      <div className="card pageNarrow wFull">

        {view === "probing" && (
          <div className="muted">Accepting invite…</div>
        )}

        {view === "accepted" && result && (
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>Invite accepted</div>
            <div className="muted" style={{ marginBottom: 16 }}>
              You've been added as a {roleLabel}.
            </div>
            <Link to="/admin" className="btn btnPrimary">Go to Hub</Link>
          </div>
        )}

        {view === "login_required" && (
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>Sign in to continue</div>
            {inviteEmail && <div className="muted" style={{ marginBottom: 16 }}>{inviteEmail}</div>}
            <Link
              to={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}
              className="btn btnPrimary"
            >
              Sign in
            </Link>
          </div>
        )}

        {view === "password_required" && (
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>Create your password</div>
            {inviteEmail && <div className="muted" style={{ marginBottom: 12 }}>{inviteEmail}</div>}
            <form onSubmit={(e) => { e.preventDefault(); void tryAccept(password); }} style={{ display: "grid", gap: 10 }}>
              <label className="field">
                <span className="label">Password</span>
                <PasswordInput value={password} onChange={setPassword} autoComplete="new-password" aria-label="Password" />
              </label>
              <button type="submit" disabled={loading || password.length < 8} className="btn btnPrimary">
                Accept invite
              </button>
            </form>
          </div>
        )}

        {view === "forbidden" && (
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>Wrong account</div>
            <div className="muted" style={{ marginBottom: 16 }}>
              You're signed in as a different account. Sign out and try again.
            </div>
            <button
              className="btn"
              type="button"
              onClick={async () => {
                await apiFetch("/api/auth/logout", { method: "POST" });
                window.location.reload();
              }}
            >
              Sign out
            </button>
          </div>
        )}

        {view === "error" && (
          <div>
            <div style={{ fontWeight: 900, fontSize: 18, letterSpacing: "-0.015em", marginBottom: 6 }}>Something went wrong</div>
            <div className="muted" style={{ marginBottom: 16 }}>{error}</div>
            <Link to="/login" className="btn btnPrimary">Sign in</Link>
          </div>
        )}

      </div>
    </div>
  );
}
